import * as EventSystemAPI from "@/API/event-system.js";
import {
  canClaim,
  eventState,
  isComplete,
  showBadgeBubble,
} from "@/utils/eventSystem.js";

const getDefaultState = () => {
  return {
    eventsById: {},
    activeIds: [],
    allIds: [],
    loading: false,
    // Rewards returned by the last successful claim, for the reward show.
    lastClaimedRewards: null,
  };
};

const state = getDefaultState();

// Endpoints take an id or a slug, but the map is keyed by id only.
const keyOf = (event) => event && event.id;
let activityTimer = null;

const mutations = {
  resetState(state) {
    Object.assign(state, getDefaultState());
  },

  setLoading(state, loading) {
    state.loading = !!loading;
  },

  // Merge, so a single-event fetch never drops fields a list fetch supplied.
  upsertEvent(state, event) {
    const id = keyOf(event);
    if (id == null) return;

    state.eventsById = {
      ...state.eventsById,
      [id]: { ...(state.eventsById[id] || {}), ...event },
    };
  },

  setActiveIds(state, ids) {
    state.activeIds = ids || [];
  },

  setAllIds(state, ids) {
    state.allIds = ids || [];
  },

  setLastClaimedRewards(state, rewards) {
    state.lastClaimedRewards = rewards || null;
  },
};

const getters = {
  isLoading: (state) => state.loading,

  eventById: (state) => (id) => state.eventsById[id] || null,

  // Endpoints accept a slug too, so resolve either way.
  eventByKey: (state) => (key) => {
    if (key == null) return null;
    if (state.eventsById[key]) return state.eventsById[key];
    return (
      Object.values(state.eventsById).find((e) => e && e.slug === key) || null
    );
  },

  activeEvents: (state) =>
    state.activeIds.map((id) => state.eventsById[id]).filter(Boolean),

  allEvents: (state) =>
    state.allIds.map((id) => state.eventsById[id]).filter(Boolean),

  // Badge rail: active events, highest priority first.
  badgeEvents: (state, getters) =>
    [...getters.activeEvents].sort(
      (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
    ),

  // Red bubble on the rail as a whole -- any unseen event, or any reward
  // waiting to be claimed.
  hasBadgeBubble: (state, getters) =>
    getters.activeEvents.some((e) => showBadgeBubble(e) || canClaim(e)),

  stateOfEvent: () => (event) => eventState(event),

  lastClaimedRewards: (state) => state.lastClaimedRewards,
};

// Both list endpoints answer `{ events: [...] }`; the rest answer
// `{ event: {...} }`. Normalise defensively in case that ever shifts.
const eventsFrom = (data) => (data && data.events) || [];
const eventFrom = (data) => (data && data.event) || null;

const actions = {
  // Called on player activity (spin, level up). There is no websocket event
  // for event progress, so refetch shortly after -- only while some active
  // event is still below 100%.
  refreshAfterActivity({ dispatch, getters }) {
    if (activityTimer) return;
    if (!getters.activeEvents.some((event) => !isComplete(event))) return;
    activityTimer = setTimeout(() => {
      activityTimer = null;
      dispatch("fetchActiveEvents");
    }, 3000);
  },

  fetchActiveEvents({ commit }) {
    commit("setLoading", true);
    return EventSystemAPI.getActiveEvents()
      .then((data) => {
        const events = eventsFrom(data);
        events.forEach((event) => commit("upsertEvent", event));
        commit(
          "setActiveIds",
          events.map(keyOf).filter((id) => id != null)
        );
        return events;
      })
      .catch(() => [])
      .finally(() => commit("setLoading", false));
  },

  fetchAllEvents({ commit }) {
    commit("setLoading", true);
    return EventSystemAPI.getAllEvents()
      .then((data) => {
        const events = eventsFrom(data);
        events.forEach((event) => commit("upsertEvent", event));
        commit(
          "setAllIds",
          events.map(keyOf).filter((id) => id != null)
        );
        return events;
      })
      .catch(() => [])
      .finally(() => commit("setLoading", false));
  },

  // `key` is an id or a slug.
  fetchEvent({ commit }, key) {
    if (key == null) return Promise.resolve(null);
    return EventSystemAPI.getEventById(key)
      .then((data) => {
        const event = eventFrom(data);
        if (event) commit("upsertEvent", event);
        return event;
      })
      .catch(() => null);
  },

  // Joins an `opt_in` event, debiting `opt_in_cost`. Errors are rethrown so
  // the caller can surface `err.data.id` (see EVENT_ERROR).
  optIn({ commit }, key) {
    return EventSystemAPI.optInToEvent(key).then((data) => {
      const event = eventFrom(data);
      if (event) commit("upsertEvent", event);
      return event;
    });
  },

  // In-game currency upgrade only -- debits `premium.cost` and applies
  // `premium.boost` once. For a real-money event buy `premium.pay_package_id`
  // through the payment flow instead, then `fetchEvent` to pick up the
  // asynchronously granted premium.
  upgradeToPremium({ commit }, key) {
    return EventSystemAPI.upgradeEventToPremium(key).then((data) => {
      const event = eventFrom(data);
      if (event) commit("upsertEvent", event);
      return event;
    });
  },

  // Books free rewards, plus premium ones when the player upgraded.
  claim({ commit }, key) {
    return EventSystemAPI.claimEventRewards(key).then((data) => {
      const event = eventFrom(data);
      if (event) commit("upsertEvent", event);
      commit("setLastClaimedRewards", (data && data.rewards) || []);
      return data;
    });
  },

  // Clears the badge's red bubble. Idempotent, and failure is not worth
  // bothering the player with.
  markSeen({ commit }, key) {
    return EventSystemAPI.markEventSeen(key)
      .then((data) => {
        const event = eventFrom(data);
        if (event) commit("upsertEvent", event);
        return event;
      })
      .catch(() => null);
  },

  clearLastClaimedRewards({ commit }) {
    commit("setLastClaimedRewards", null);
  },

  resetEventSystemState({ commit }) {
    commit("resetState");
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
